/* Sentripol BE — the review lifecycle.
 *
 * Everything the single-file build produced lived in one browser tab and died
 * with it: reload the page and the analysis was gone. These calls give a
 * review an id, keep the extracted page text and the seven derived objects,
 * and let a report be fetched back later.
 *
 * The extracted TEXT is what is sent, not the PDFs, and that is deliberate.
 * The browser extracts with PDF.js and the server would use pypdf or
 * LlamaParse; the backend's own extraction-diff harness shows those two
 * disagree on this corpus about triggers, endorsement counts and one limit
 * key. Until that gate passes, the client's text is the text the report is
 * built from — so a persisted report matches what the broker saw.
 *
 * Neither the text nor the report is retained. The text goes up with the
 * analysis request and the backend discards it when the request ends; the
 * report comes back in the response and is stored nowhere, because a grounded
 * report quotes the policy and keeping it would be the same retention with an
 * extra step. A policy set is the most confidential thing a broker handles.
 *
 * What is kept is the structured finding — the seven derived objects, with
 * long policy prose stripped — plus which documents were reviewed, a hash of
 * their text, and the fact that an analysis ran.
 */

import { redactForStorage } from 'sentripol-engine/redact';
import { authHeader } from './session.js';

const base = () => {
  const b = (typeof window !== 'undefined' && window.SENTRIPOL_CONFIG || {}).backend;
  return (b == null ? '' : b).replace(/\/+$/, '');
};

async function call(method, path, body, ms = 120_000) {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), ms);
  try {
    // Every route on the backend except /health requires a real Entra ID
    // access token. Without this header the answer is 401, which is the
    // point: the service no longer trusts whoever can reach the port.
    const res = await fetch(base() + path, {
      method,
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(await authHeader())
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: ctl.signal
    });
    const text = await res.text();
    let data; try { data = JSON.parse(text); } catch { data = text; }
    if (!res.ok) {
      // A 401 means the session lapsed mid-review, which is a different
      // problem from the call failing, and the user needs to be told to sign
      // in rather than shown a raw API error.
      if (res.status === 401) throw new Error('Your session has expired. Reload the page to sign in again.');
      const msg = (data && data.error) || `${method} ${path} failed (${res.status})`;
      throw new Error(msg);
    }
    return data;
  } finally {
    clearTimeout(timer);
  }
}

export const isEnabled = () =>
  (typeof window !== 'undefined' && window.SENTRIPOL_CONFIG || {}).persistReviews !== false;

export const createReview = (fields) => call('POST', '/api/reviews', fields);

export const getReview = (id) => call('GET', `/api/reviews/${id}`);
export const getDerived = (id) => call('GET', `/api/reviews/${id}/derived`);
// No getReport: the backend answers 410 there, because nothing is stored to
// fetch. What can be read back is that an analysis ran, and what it found.
export const getAnalyses = (id) => call('GET', `/api/reviews/${id}/analyses`);

/* Record the classification the report was built from.
 *
 * The page has already classified — spDetectProfileSmart called
 * /api/analyze/profile and every returned item went through the grounding
 * gate here in the browser — so this sends that answer rather than asking the
 * backend to produce another one. Re-classifying server-side could return
 * something different, and a record that disagrees with the report it
 * describes is worse than no record.
 */
export const recordClassification = (id, body) =>
  call('PUT', `/api/reviews/${id}/classification`, body, 30_000);

/* What the engine left on the window after classifying. Shapes differ between
 * the profile detector and the NAICS pivot, so they are read defensively:
 * either can be absent when its call failed, and a missing classification
 * must not lose the review. */
export function classificationFromWindow() {
  const w = typeof window !== 'undefined' ? window : {};
  const profile = w._detectedProfile || {};
  const naics = (w._naicsAnalysis && w._naicsAnalysis.naics) || {};
  const verticals = Array.isArray(profile.verticals) ? profile.verticals : [];
  const states = Array.isArray(profile.states) ? profile.states : [];
  if (!verticals.length && !states.length && !naics.code) return null;
  return {
    naics: naics.code || null,
    vertical: naics.vertical || verticals[0]?.name || null,
    verticals, states,
    method: profile.method || null,
    model: naics.source || null,
    evidence: { policyOnlyStates: profile.policyOnlyStates || [] }
  };
}

/* The seven objects the engine derives. The browser has them already: it
 * derived them to render the report that is on screen, and they are sitting
 * on window._lastFormData. */
const DERIVED_KEYS = ['declLimits', 'policyTerms', 'endorsements', 'triggers',
                      'stateCompliance', 'deepScanResults', 'deepScanEOResults'];

export function derivedFromWindow() {
  const fd = (typeof window !== 'undefined' && window._lastFormData) || null;
  if (!fd) return null;
  const out = {};
  for (const k of DERIVED_KEYS) if (fd[k] !== undefined && fd[k] !== null) out[k] = fd[k];
  return Object.keys(out).length ? out : null;
}

/* sha256 of a document's extracted text, computed here.
 *
 * The backend hashes the text when it is given the text. When it is not, the
 * hash has to come from the only side that has it. Same input either way, the
 * pages joined by newlines, so the two paths produce the same digest for the
 * same document. */
async function hashText(pages) {
  const text = (pages || []).map(p => p.text).join('\n');
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
}

/* Send the analysis, not the documents.
 *
 * The engine already ran, in this browser, to produce the report on screen.
 * Having the backend derive the same seven objects from the same text was the
 * same work done twice with one result thrown away. So the findings go up and
 * the policy text does not go up at all: what crosses the wire is a filename,
 * a page count and a hash.
 *
 * Falls back to sending the text when the derived objects are missing, which
 * happens if the engine failed partway. Then the backend does the work and the
 * review is still recorded.
 */
export async function analyze(id, pdfTexts, lob, derived) {
  const usable = pdfTexts || [];
  if (derived) {
    const documents = await Promise.all(usable.map(async (doc) => ({
      filename: doc.fileName || doc.name,
      page_count: (doc.pages || []).length,
      extractor: 'pdfjs',
      byte_size: doc.size || null,
      text_sha256: await hashText(doc.pages)
    })));
    // Strip the policy prose HERE, not on arrival. The backend strips again
    // before storing, but by then the language has already crossed the wire —
    // and a review of five policies carried about 300 kB of it. Redacting
    // first means what leaves the browser is the finding and nothing else.
    // A copy, because these objects are the ones the report was built from.
    return call('POST', `/api/reviews/${id}/analyze?lob=${encodeURIComponent(lob)}`,
      { documents, derived: redactForStorage(derived), report_chars: reportLength() }, 60_000);
  }
  return call('POST', `/api/reviews/${id}/analyze?lob=${encodeURIComponent(lob)}`, {
    documents: usable.map(doc => ({
      filename: doc.fileName || doc.name,
      pages: doc.pages,
      extractor: 'pdfjs',
      byte_size: doc.size || null
    }))
  }, 300_000);
}

/* How long the report on screen is. Recorded so a report that suddenly halves
 * is noticeable later; it carries no policy language. */
function reportLength() {
  try {
    const tab = document.querySelector('.tab-content.active');
    return tab ? tab.innerHTML.length : 0;
  } catch { return 0; }
}

/* Record a completed analysis.
 *
 * Best-effort by design: the report is already on screen, generated locally,
 * and losing persistence must not lose the review the broker is reading. Every
 * failure is returned rather than thrown, so the caller can surface it without
 * the analysis itself failing.
 */
export async function persistReview({ form, pdfTexts }) {
  if (!isEnabled()) return { ok: false, skipped: true };
  try {
    const review = await createReview({
      policyholder_name: form.policyholderName,
      website_url: form.websiteUrl || null,
      org_overview: form.orgOverview || null,
      incumbent: form.incumbent || null,
      case_nick: form.caseNick || null,
      lob: (form.lob || 'pc').toLowerCase()
    });

    // Everything readable goes up in one request: the analysis needs the whole
    // set, and a document sent on its own would be text the backend holds for
    // no reason.
    const usable = (pdfTexts || []).filter(d => d && Array.isArray(d.pages) && d.pages.length);
    if (!usable.length) return { ok: false, error: 'no readable document text to analyse' };

    let analysis = null;
    try {
      // Send what the engine already produced. Only if it produced nothing
      // does the backend get the text and do the work again.
      analysis = await analyze(review.id, usable, (form.lob || 'pc').toLowerCase(),
                               derivedFromWindow());
    } catch (e) {
      // The review exists and records which documents were seen; the analysis
      // did not run. Worth saying, and not worth losing the report on screen.
      return { ok: false, id: review.id, error: e.message };
    }

    // The classification the report used, recorded after it. A failure here
    // costs the record of how the vertical was reached, not the review.
    let classified = null;
    const cls = classificationFromWindow();
    if (cls) {
      try { classified = await recordClassification(review.id, cls); }
      catch (e) { console.warn('[review] classification not recorded: ' + e.message); }
    }

    return {
      ok: true, id: review.id,
      attached: usable.map(d => d.fileName),
      derived: analysis && analysis.derived,
      classified
    };
  } catch (e) {
    return { ok: false, error: e.message };
  }
}
